import type {
  AriaLabelingProps,
  DOMProps,
  FocusableProps,
  HoverEvents,
  LinkDOMProps,
  PressEvents,
} from '@react-types/shared';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import type { LinkProps as AriaLinkProps } from 'react-aria-components';
import { Link as AriaLink } from 'react-aria-components';

import type { UnsafeStyleProps } from '@infinex/ui-styles';

export type LinkProps = {
  /** The content of the element. */
  children: ReactNode;

  /** Whether the element is disabled. */
  isDisabled?: boolean;
  /**
   * Optionally provide a title for the link. Useful in cases where you expect
   * the content to be truncated.
   */
  title?: string;
} & LinkDOMProps &
  AriaLabelingProps &
  DOMProps &
  FocusableProps &
  HoverEvents &
  PressEvents &
  UnsafeStyleProps;

/**
 * A wrapper around the HTML anchor element, which supports client-side
 * navigation between pages.
 *
 * [Taurus docs: `Link`](https://ui-sb.app.infinex.dev/?path=/docs/components-link--docs)
 */
export const Link = forwardRef<
  HTMLAnchorElement,
  // support function props for this generic component, while keeping reusable
  // `LinkProps` as the base type
  LinkProps & Pick<AriaLinkProps, 'className' | 'style'>
>(function Link(props, ref) {
  return <AriaLink ref={ref} {...props} />;
});
